<script setup lang="ts">
import { AppButton, AppLazyLoadComponent, SemboIllustration } from '@libs/ui';

interface HotelStayCardImageProps {
  shouldShowOpenGalleryButton: boolean;
  imageUrl?: string;
}

const props = defineProps<HotelStayCardImageProps>();

interface HotelStayCardImageEmits {
  (eventName: 'open-gallery'): void;
}

const emit = defineEmits<HotelStayCardImageEmits>();
</script>

<template>
  <div class="hotel-stay-card-image">
    <AppLazyLoadComponent v-if="props.imageUrl">
      <img class="image" :alt="$t('a11y.room_image')" :src="props.imageUrl" />
    </AppLazyLoadComponent>
    <div v-else class="image alternative-illustration">
      <SemboIllustration />
      <span>
        {{ $t('planner.no_availability.messages.room_image_coming_soon') }}
      </span>
    </div>

    <AppButton
      v-if="props.shouldShowOpenGalleryButton"
      state="enabled"
      size="medium"
      :icon="{ name: 'zoom' }"
      type="secondary"
      class="open-gallery-button"
      @click="emit('open-gallery')"
    />
  </div>
</template>

<style scoped lang="scss">
.hotel-stay-card-image {
  max-width: 100%;
  overflow: hidden;
  position: relative;

  .image {
    width: 100%;
    aspect-ratio: 2 / 1;
    object-fit: cover;
    justify-content: center;
    height: auto;
    border-radius: var(--border-radius-8) var(--border-radius-8) 0 0;

    @include for-less-or-equal-than-599-px {
      aspect-ratio: 16/9;
    }
    &.alternative-illustration {
      background-color: var(--color-neutral-10);
      border-bottom: 1px solid var(--color-neutral-30);
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--spacing-16);

      @include font-label-s-light;
      color: var(--color-neutral-70);
      text-align: center;
    }
  }

  .open-gallery-button {
    position: absolute;
    top: var(--spacing-8);
    right: var(--spacing-8);
  }
}
</style>
